import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MarkdownEditor } from '../components/MarkdownEditor';
import {
  LayoutDashboard, Inbox, Users, Package, Activity, Send, UserPlus,
  RefreshCw, Loader, Search, Download, Mail, Copy, Check, Trash2,
  Key, Shield, ShieldOff, Plus, X, AlertTriangle, RotateCw,
  UploadCloud, FileCode, FileImage, FileText, File as FileIcon, Folder, Save,
  Briefcase, Eye,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN = `${API}/admin`;

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const SECTIONS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'contacts', label: 'Inbox', icon: Inbox },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'widgets', label: 'Widgets', icon: Package },
  { id: 'careers', label: 'Careers', icon: Briefcase },
  { id: 'audit', label: 'Audit Log', icon: Activity },
  { id: 'blast', label: 'Email Blast', icon: Send },
];

const StatusBadge = ({ status }) => {
  const colors = {
    unread: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    read: 'bg-gray-500/15 text-gray-300 border-gray-500/30',
    replied: 'bg-green-500/15 text-green-300 border-green-500/30',
    archived: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',
  };
  const cls = colors[status] || colors.unread;
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${cls}`}>
      {status || 'unread'}
    </span>
  );
};

export const AdminConsole = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState('overview');

  useEffect(() => {
    if (!authLoading) {
      if (!user || user === false) navigate('/login');
      else if (user.role !== 'admin') navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  if (authLoading || !user || user.role !== 'admin') {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center">
        <Loader className="w-7 h-7 accent-green animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-80px)]" data-testid="admin-console">
      <section className="py-10 px-4 sm:px-6 lg:px-8 hero-gradient">
        <div className="max-w-7xl mx-auto">
          <span className="eyebrow">Admin · Console</span>
          <h1 className="text-3xl sm:text-4xl font-bold mt-2 mb-2 text-strong">
            Hey <span className="text-gradient-brand">{user.name || 'Admin'}</span>
          </h1>
          <p className="text-sm text-muted">Run LAB21g from one place.</p>
        </div>
      </section>

      <div className="border-y border-subtle sticky top-[68px] z-30" style={{ background: 'var(--bg-base)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex overflow-x-auto">
          {SECTIONS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setSection(id)}
              data-testid={`admin-tab-${id}`}
              className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                section === id
                  ? 'border-[#39FF14] text-strong'
                  : 'border-transparent text-muted hover:text-strong'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {section === 'overview' && <OverviewTab />}
        {section === 'contacts' && <ContactsTab />}
        {section === 'users' && <UsersTab />}
        {section === 'widgets' && <WidgetsTab />}
        {section === 'careers' && <CareersTab />}
        {section === 'audit' && <AuditTab />}
        {section === 'blast' && <BlastTab />}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Overview
// ─────────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub }) => (
  <div className="card-surface p-5">
    <div className="text-xs uppercase tracking-wider text-faded mb-2">{label}</div>
    <div className="text-3xl font-bold text-strong">{value}</div>
    {sub && <div className="text-xs text-muted mt-1">{sub}</div>}
  </div>
);

const OverviewTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${ADMIN}/overview`, { withCredentials: true });
      setData(d);
    } catch (e) {
      console.error('overview load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) return <div className="text-center py-10"><Loader className="w-6 h-6 accent-green animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Users" value={data.users.total} sub={`${data.users.admins} admin${data.users.admins === 1 ? '' : 's'}`} />
        <StatCard label="Active widgets" value={data.widgets.active_subscriptions} sub={`${data.widgets.total} in catalog`} />
        <StatCard label="Contacts · 30d" value={data.contacts.last_30_days} sub={`${data.contacts.unread} unread`} />
        <StatCard label="Revenue (paid)" value={`$${data.revenue.total_usd.toFixed(2)}`} sub="Stripe paid orders" />
      </div>

      <div className="card-surface p-6">
        <h2 className="text-lg font-semibold text-strong mb-4">Recent activity</h2>
        {data.recent_activity.length === 0 ? (
          <p className="text-sm text-muted">Nothing yet.</p>
        ) : (
          <ul className="space-y-3">
            {data.recent_activity.map((a, i) => (
              <li key={i} className="flex items-start justify-between text-sm border-b border-subtle pb-3 last:border-0">
                <div className="flex-1">
                  {a.type === 'contact' ? (
                    <span className="text-default">
                      <span className="accent-cyan">Contact:</span> <strong className="text-strong">{a.name}</strong> ({a.email})
                    </span>
                  ) : (
                    <span className="text-default">
                      <span className="accent-green">{a.actor}</span> · {a.action} {a.target_type ? `→ ${a.target_type}` : ''}
                    </span>
                  )}
                </div>
                <span className="text-faded text-xs ml-3 shrink-0">{fmtDate(a.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Contacts
// ─────────────────────────────────────────────────────────────────────
const ContactsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(120);
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days });
      if (status !== 'all') params.set('status', status);
      if (q) params.set('q', q);
      const { data } = await axios.get(`${ADMIN}/contacts?${params}`, { withCredentials: true });
      setItems(data.items || []);
    } catch (e) {
      console.error('contacts load failed', e);
    } finally {
      setLoading(false);
    }
  }, [days, status, q]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setItemStatus = async (id, newStatus) => {
    await axios.patch(`${ADMIN}/contacts/${id}`, { status: newStatus }, { withCredentials: true });
    fetchData();
  };

  const copyEmail = (email, id) => {
    navigator.clipboard.writeText(email);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const exportCsv = async () => {
    const url = `${ADMIN}/contacts/export.csv?days=${days}`;
    window.open(url, '_blank');
  };

  return (
    <div data-testid="admin-contacts">
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <label className="block text-xs text-faded mb-1">Days</label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="input-field px-3 py-2 text-sm" data-testid="contacts-days-filter">
            <option value={30}>Last 30</option>
            <option value={120}>Last 120</option>
            <option value={365}>Last 365</option>
            <option value={3650}>All time</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-faded mb-1">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="input-field px-3 py-2 text-sm" data-testid="contacts-status-filter">
            <option value="all">All</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
            <option value="replied">Replied</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-faded mb-1">Search</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-faded" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email or message…" className="w-full input-field pl-9 pr-3 py-2 text-sm" data-testid="contacts-search-input" />
          </div>
        </div>
        <button onClick={exportCsv} className="btn-secondary px-4 py-2 text-sm inline-flex items-center space-x-2" data-testid="contacts-export-csv">
          <Download className="w-4 h-4" /><span>CSV</span>
        </button>
        <button onClick={fetchData} className="btn-secondary px-4 py-2 text-sm inline-flex items-center space-x-2" data-testid="contacts-refresh">
          <RefreshCw className="w-4 h-4" /><span>Refresh</span>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader className="w-6 h-6 accent-green animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 card-surface">
          <Inbox className="w-10 h-10 text-faded mx-auto mb-3" />
          <p className="text-sm text-muted">No messages match the current filters.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="contacts-list">
          {items.map((it) => (
            <div key={it.id} className="card-surface p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base font-semibold text-strong">{it.name || '(no name)'}</h3>
                    <StatusBadge status={it.status} />
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-3.5 h-3.5 text-faded" />
                    <a href={`mailto:${it.email}`} className="text-default hover:accent-green">{it.email}</a>
                    <button onClick={() => copyEmail(it.email, it.id)} className="text-faded hover:text-strong p-0.5">
                      {copiedId === it.id ? <Check className="w-3.5 h-3.5 accent-green" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
                <span className="text-xs text-faded">{fmtDate(it.created_at)}</span>
              </div>
              <p className="text-sm text-default whitespace-pre-wrap leading-relaxed mb-3">{it.message}</p>
              <div className="flex flex-wrap gap-2">
                {['read', 'replied', 'archived', 'unread'].filter(s => s !== it.status).map(s => (
                  <button key={s} onClick={() => setItemStatus(it.id, s)} className="text-[11px] uppercase tracking-wider px-2 py-1 rounded border border-default text-muted hover:text-strong hover:border-emphasis transition-colors" data-testid={`contact-mark-${s}-${it.id}`}>
                    Mark {s}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Users
// ─────────────────────────────────────────────────────────────────────
const UsersTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [widgetOptions, setWidgetOptions] = useState([]);
  const [feedback, setFeedback] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = q ? `?q=${encodeURIComponent(q)}` : '';
      const { data } = await axios.get(`${ADMIN}/users${params}`, { withCredentials: true });
      setItems(data.items || []);
    } catch (e) {
      console.error('users load failed', e);
    } finally {
      setLoading(false);
    }
  }, [q]);

  const fetchWidgets = useCallback(async () => {
    try {
      const { data } = await axios.get(`${ADMIN}/widgets`, { withCredentials: true });
      setWidgetOptions(data.items || []);
    } catch (e) {
      console.error('widgets load failed', e);
    }
  }, []);

  useEffect(() => { fetchUsers(); fetchWidgets(); }, [fetchUsers, fetchWidgets]);

  const showFeedback = (msg, kind = 'ok') => {
    setFeedback({ msg, kind });
    setTimeout(() => setFeedback(null), 5000);
  };

  return (
    <div data-testid="admin-users">
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-faded mb-1">Search</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-faded" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="w-full input-field pl-9 pr-3 py-2 text-sm" data-testid="users-search-input" />
          </div>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary px-4 py-2 text-sm inline-flex items-center space-x-2" data-testid="users-create-button">
          <UserPlus className="w-4 h-4" /><span>New user</span>
        </button>
        <button onClick={fetchUsers} className="btn-secondary px-4 py-2 text-sm inline-flex items-center space-x-2">
          <RefreshCw className="w-4 h-4" /><span>Refresh</span>
        </button>
      </div>

      {feedback && (
        <div className={`mb-4 p-3 rounded text-sm border ${feedback.kind === 'ok' ? 'bg-green-500/10 border-green-500/30 text-green-300' : 'bg-red-500/10 border-red-500/30 text-red-300'}`}>
          {feedback.msg}
        </div>
      )}

      {loading ? (
        <div className="text-center py-10"><Loader className="w-6 h-6 accent-green animate-spin mx-auto" /></div>
      ) : (
        <div className="card-surface overflow-hidden" data-testid="users-list">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-faded border-b border-subtle">
              <tr>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Role</th>
                <th className="px-5 py-3">Widgets</th>
                <th className="px-5 py-3">Joined</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <React.Fragment key={u.id}>
                  <tr className="border-b border-subtle hover:bg-white/[0.02] cursor-pointer" onClick={() => setOpenId(openId === u.id ? null : u.id)} data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3 text-strong font-medium">{u.name || '—'}</td>
                    <td className="px-5 py-3 text-default">{u.email}</td>
                    <td className="px-5 py-3">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${u.role === 'admin' ? 'bg-purple-500/15 text-purple-300 border-purple-500/30' : 'bg-gray-500/15 text-gray-300 border-gray-500/30'}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-default">{u.active_widget_count}</td>
                    <td className="px-5 py-3 text-faded">{fmtDate(u.created_at)}</td>
                    <td className="px-5 py-3 text-right">
                      <button className="text-xs text-muted hover:text-strong">{openId === u.id ? 'Close' : 'Manage'}</button>
                    </td>
                  </tr>
                  {openId === u.id && (
                    <tr className="border-b border-subtle">
                      <td colSpan={6} className="px-5 py-5" style={{ background: 'var(--bg-elev-1)' }}>
                        <UserDetailPanel userId={u.id} widgetOptions={widgetOptions} onChange={() => { fetchUsers(); }} onFeedback={showFeedback} currentUserRole={u.role} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-muted text-sm">No users match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchUsers(); showFeedback('User created.'); }}
        />
      )}
    </div>
  );
};

const UserDetailPanel = ({ userId, widgetOptions, onChange, onFeedback, currentUserRole }) => {
  const [detail, setDetail] = useState(null);
  const [grantId, setGrantId] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const fetchDetail = useCallback(async () => {
    try {
      const { data } = await axios.get(`${ADMIN}/users/${userId}`, { withCredentials: true });
      setDetail(data);
    } catch (e) {
      console.error('user detail failed', e);
    }
  }, [userId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  if (!detail) return <div className="text-sm text-muted">Loading...</div>;

  const grantWidget = async () => {
    if (!grantId) return;
    try {
      await axios.post(`${ADMIN}/users/${userId}/grant-widget`, { widget_id: grantId }, { withCredentials: true });
      setGrantId('');
      fetchDetail();
      onChange();
      onFeedback(`Widget granted to ${detail.email}.`);
    } catch (e) {
      onFeedback(e.response?.data?.detail || 'Failed to grant widget.', 'err');
    }
  };

  const revokeWidget = async (purchaseId) => {
    if (!window.confirm('Revoke this widget?')) return;
    await axios.delete(`${ADMIN}/users/${userId}/widgets/${purchaseId}`, { withCredentials: true });
    fetchDetail();
    onChange();
    onFeedback('Widget revoked.');
  };

  const rotateToken = async (purchaseId) => {
    if (!window.confirm('Generate a new OBS URL? The old one stops working immediately.')) return;
    await axios.post(`${ADMIN}/purchases/${purchaseId}/rotate-token`, {}, { withCredentials: true });
    fetchDetail();
    onFeedback('OBS token rotated.');
  };

  const resetPassword = async () => {
    try {
      const { data } = await axios.patch(`${ADMIN}/users/${userId}`, { password: '' }, { withCredentials: true });
      onFeedback(`Temp password: ${data.temp_password} — give it to the user.`);
    } catch (e) {
      onFeedback('Reset failed.', 'err');
    }
  };

  const toggleRole = async () => {
    const newRole = currentUserRole === 'admin' ? 'user' : 'admin';
    try {
      await axios.patch(`${ADMIN}/users/${userId}`, { role: newRole }, { withCredentials: true });
      fetchDetail();
      onChange();
      onFeedback(`Role changed to ${newRole}.`);
    } catch (e) {
      onFeedback('Role change failed.', 'err');
    }
  };

  const deleteUser = async () => {
    try {
      await axios.delete(`${ADMIN}/users/${userId}`, { withCredentials: true });
      onChange();
      onFeedback('User deleted.');
    } catch (e) {
      onFeedback(e.response?.data?.detail || 'Delete failed.', 'err');
    }
  };

  return (
    <div className="space-y-5" data-testid={`user-detail-${userId}`}>
      <div className="flex flex-wrap gap-2">
        <button onClick={resetPassword} className="btn-secondary px-3 py-1.5 text-xs inline-flex items-center space-x-1.5" data-testid={`user-reset-pw-${userId}`}>
          <Key className="w-3.5 h-3.5" /><span>Reset password</span>
        </button>
        <button onClick={toggleRole} className="btn-secondary px-3 py-1.5 text-xs inline-flex items-center space-x-1.5">
          {currentUserRole === 'admin' ? <><ShieldOff className="w-3.5 h-3.5" /><span>Demote to user</span></> : <><Shield className="w-3.5 h-3.5" /><span>Promote to admin</span></>}
        </button>
        {!confirmDelete ? (
          <button onClick={() => setConfirmDelete(true)} className="px-3 py-1.5 text-xs inline-flex items-center space-x-1.5 border border-red-500/30 text-red-300 rounded hover:bg-red-500/10">
            <Trash2 className="w-3.5 h-3.5" /><span>Delete user</span>
          </button>
        ) : (
          <>
            <button onClick={deleteUser} className="px-3 py-1.5 text-xs inline-flex items-center space-x-1.5 bg-red-500/20 border border-red-500/40 text-red-200 rounded">
              <AlertTriangle className="w-3.5 h-3.5" /><span>Confirm delete</span>
            </button>
            <button onClick={() => setConfirmDelete(false)} className="px-3 py-1.5 text-xs text-muted">Cancel</button>
          </>
        )}
      </div>

      <div>
        <h4 className="text-xs uppercase tracking-wider text-faded mb-3">Subscriptions ({detail.purchases.length})</h4>
        {detail.purchases.length === 0 ? (
          <p className="text-sm text-muted">No subscriptions yet.</p>
        ) : (
          <div className="space-y-2">
            {detail.purchases.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 p-3 rounded border border-subtle" style={{ background: 'var(--bg-base)' }}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-strong">{p.widget_name}</div>
                  <div className="text-xs text-faded">
                    {p.status} · via {p.payment_method || 'unknown'} · {fmtDate(p.created_at)}
                  </div>
                  {p.obs_link && (
                    <div className="text-[11px] text-muted mt-1 truncate font-mono">{p.obs_link}</div>
                  )}
                </div>
                <div className="flex gap-2">
                  {p.obs_link && (
                    <button onClick={() => { navigator.clipboard.writeText(p.obs_link); onFeedback('OBS link copied.'); }} className="text-xs px-2 py-1 border border-default rounded text-muted hover:text-strong inline-flex items-center space-x-1">
                      <Copy className="w-3 h-3" /><span>Copy URL</span>
                    </button>
                  )}
                  {p.status === 'active' && (
                    <>
                      <button onClick={() => rotateToken(p.id)} className="text-xs px-2 py-1 border border-default rounded text-muted hover:text-strong inline-flex items-center space-x-1">
                        <RotateCw className="w-3 h-3" /><span>Rotate</span>
                      </button>
                      <button onClick={() => revokeWidget(p.id)} className="text-xs px-2 py-1 border border-red-500/30 text-red-300 rounded hover:bg-red-500/10">Revoke</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h4 className="text-xs uppercase tracking-wider text-faded mb-2">Grant a widget</h4>
        <div className="flex gap-2">
          <select value={grantId} onChange={(e) => setGrantId(e.target.value)} className="input-field px-3 py-2 text-sm flex-1" data-testid={`user-grant-select-${userId}`}>
            <option value="">— pick a widget —</option>
            {widgetOptions.map((w) => (<option key={w.id} value={w.id}>{w.name}</option>))}
          </select>
          <button onClick={grantWidget} disabled={!grantId} className="btn-primary px-4 py-2 text-sm disabled:opacity-50">
            <Plus className="w-4 h-4 inline" /> Grant
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateUserModal = ({ onClose, onCreated }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${ADMIN}/users`, { email, name, password, role }, { withCredentials: true });
      onCreated();
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Failed to create user.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="create-user-modal">
      <div className="card-surface max-w-md w-full p-6">
        <div className="flex justify-between items-start mb-5">
          <h3 className="text-lg font-semibold text-strong">Create user</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-faded" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3" data-testid="create-user-form">
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="Email" className="w-full input-field px-3 py-2 text-sm" data-testid="create-user-email" />
          <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Name" className="w-full input-field px-3 py-2 text-sm" data-testid="create-user-name" />
          <input value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Password" className="w-full input-field px-3 py-2 text-sm" data-testid="create-user-password" />
          <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full input-field px-3 py-2 text-sm">
            <option value="user">user</option>
            <option value="admin">admin</option>
          </select>
          {error && <p className="text-sm text-red-300">{error}</p>}
          <button type="submit" disabled={submitting} className="w-full btn-primary px-4 py-2.5 text-sm disabled:opacity-50" data-testid="create-user-submit">
            {submitting ? 'Creating…' : 'Create user'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Widgets catalog + file manager
// ─────────────────────────────────────────────────────────────────────
const fmtBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const fileIconFor = (ext) => {
  if (ext === '.html' || ext === '.htm') return FileCode;
  if (['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'].includes(ext)) return FileImage;
  if (['.css', '.js', '.json', '.txt', '.md', '.xml'].includes(ext)) return FileText;
  return FileIcon;
};

const WidgetsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const fetchWidgets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${ADMIN}/widgets`, { withCredentials: true });
      // Defensive dedupe by id in case of stale duplicates from upstream
      const seen = new Set();
      const unique = (data.items || []).filter((w) => {
        if (seen.has(w.id)) return false;
        seen.add(w.id);
        return true;
      });
      setItems(unique);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchWidgets(); }, [fetchWidgets]);

  const showFeedback = (msg, kind = 'ok') => {
    setFeedback({ msg, kind });
    setTimeout(() => setFeedback(null), 5000);
  };

  const togglePublish = async (w) => {
    try {
      await axios.patch(`${ADMIN}/widgets/${w.id}/config`, { published: !w.published }, { withCredentials: true });
      showFeedback(w.published ? 'Widget unpublished.' : 'Widget published — now visible to customers.');
      fetchWidgets();
    } catch (e) {
      showFeedback(e.response?.data?.detail || 'Failed to toggle.', 'err');
    }
  };

  const deleteWidget = async (w) => {
    if (!window.confirm(`Delete "${w.name}" and all its files? Active subscriptions will be revoked. This can't be undone.`)) return;
    try {
      await axios.delete(`${ADMIN}/widgets/${w.id}`, { withCredentials: true });
      showFeedback(`Widget "${w.name}" deleted.`);
      fetchWidgets();
    } catch (e) {
      showFeedback(e.response?.data?.detail || 'Delete failed.', 'err');
    }
  };

  if (loading) return <div className="text-center py-10"><Loader className="w-6 h-6 accent-green animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-3" data-testid="admin-widgets">
      <div className="flex justify-between items-center mb-2 flex-wrap gap-3">
        <p className="text-sm text-muted">{items.length} widget{items.length === 1 ? '' : 's'} in catalog</p>
        <button
          onClick={() => setShowCreate(true)}
          className="btn-primary px-4 py-2 text-sm inline-flex items-center space-x-2"
          data-testid="widget-create-button"
        >
          <Plus className="w-4 h-4" /><span>New widget</span>
        </button>
      </div>
      {feedback && (
        <div className={`p-3 rounded text-sm border ${feedback.kind === 'ok' ? 'bg-green-500/10 border-green-500/30 text-green-300' : 'bg-red-500/10 border-red-500/30 text-red-300'}`}>
          {feedback.msg}
        </div>
      )}
      {items.map((w) => (
        <div key={w.id} className="card-surface p-5">
          <div className="flex justify-between items-start flex-wrap gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base font-semibold text-strong">{w.name}</h3>
                {w.published ? (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border bg-green-500/15 text-green-300 border-green-500/30">Published</span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border bg-yellow-500/15 text-yellow-300 border-yellow-500/30">Draft</span>
                )}
                {w.is_seed && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border bg-purple-500/15 text-purple-300 border-purple-500/30">Seed</span>
                )}
              </div>
              <p className="text-xs text-muted mt-1 font-mono">
                {w.id} · ${w.price?.toFixed(2)} · {w.active_subscribers} active subscriber{w.active_subscribers === 1 ? '' : 's'}
                {w.folder && <> · {w.folder}/</>}
                {w.html_file && <> · entry: <span className="accent-green">{w.html_file}</span></>}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => togglePublish(w)}
                className="btn-secondary px-3 py-1.5 text-xs inline-flex items-center space-x-1.5"
                data-testid={`widget-publish-toggle-${w.id}`}
              >
                {w.published
                  ? <><ShieldOff className="w-3.5 h-3.5" /><span>Unpublish</span></>
                  : <><Shield className="w-3.5 h-3.5" /><span>Publish</span></>}
              </button>
              <button
                onClick={() => setOpenId(openId === w.id ? null : w.id)}
                className="btn-secondary px-3 py-1.5 text-xs inline-flex items-center space-x-1.5"
                data-testid={`widget-toggle-${w.id}`}
              >
                <Folder className="w-3.5 h-3.5" />
                <span>{openId === w.id ? 'Close' : 'Manage files'}</span>
              </button>
              {!w.is_seed && (
                <button
                  onClick={() => deleteWidget(w)}
                  className="px-3 py-1.5 text-xs inline-flex items-center space-x-1.5 border border-red-500/30 text-red-300 rounded hover:bg-red-500/10"
                  data-testid={`widget-delete-${w.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /><span>Delete</span>
                </button>
              )}
            </div>
          </div>

          {openId === w.id && (
            <div className="mt-5 pt-5 border-t border-subtle">
              <WidgetFileManager widgetId={w.id} onFeedback={showFeedback} onConfigChange={fetchWidgets} />
              <WidgetSubscribersPanel widgetId={w.id} />
            </div>
          )}
        </div>
      ))}

      {showCreate && (
        <CreateWidgetModal
          onClose={() => setShowCreate(false)}
          onCreated={(w) => { setShowCreate(false); fetchWidgets(); showFeedback(`Created "${w.name}" as a draft. Upload your HTML next.`); setOpenId(w.id); }}
        />
      )}
    </div>
  );
};

const CreateWidgetModal = ({ onClose, onCreated }) => {
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('99.00');
  const [imageUrl, setImageUrl] = useState('');
  const [featuresText, setFeaturesText] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Auto-derive an id slug from the name as the admin types it
  const handleNameChange = (val) => {
    setName(val);
    if (!id || id === slugify(name)) setId(slugify(val));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const features = featuresText.split('\n').map((s) => s.trim()).filter(Boolean);
      const { data } = await axios.post(`${ADMIN}/widgets`, {
        id: id.trim(),
        name: name.trim(),
        description: description.trim(),
        price: parseFloat(price) || 0,
        image_url: imageUrl.trim(),
        features,
        published: false,
      }, { withCredentials: true });
      onCreated(data.widget);
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Failed to create widget.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" data-testid="create-widget-modal">
      <div className="card-surface max-w-lg w-full p-6 my-8">
        <div className="flex justify-between items-start mb-5">
          <div>
            <h3 className="text-lg font-semibold text-strong">New widget</h3>
            <p className="text-xs text-muted mt-1">Creates a draft. Upload the HTML + assets afterwards, then publish.</p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-faded" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3" data-testid="create-widget-form">
          <div>
            <label className="block text-xs uppercase tracking-wider text-faded mb-1">Display name</label>
            <input value={name} onChange={(e) => handleNameChange(e.target.value)} required placeholder="e.g. Stream Countdown Timer" className="w-full input-field px-3 py-2 text-sm" data-testid="create-widget-name" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-faded mb-1">ID (slug — used in URLs & folder)</label>
            <input value={id} onChange={(e) => setId(slugify(e.target.value))} required placeholder="stream-countdown-timer" className="w-full input-field px-3 py-2 text-sm font-mono" data-testid="create-widget-id" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-faded mb-1">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Short marketing description shown on the product card." className="w-full input-field px-3 py-2 text-sm" data-testid="create-widget-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-faded mb-1">Price (USD)</label>
              <input value={price} onChange={(e) => setPrice(e.target.value)} type="number" step="0.01" min="0" required className="w-full input-field px-3 py-2 text-sm" data-testid="create-widget-price" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-faded mb-1">Image URL (optional)</label>
              <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://…" className="w-full input-field px-3 py-2 text-sm" data-testid="create-widget-image" />
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-faded mb-1">Features (one per line)</label>
            <textarea value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} rows={5} placeholder="Real-time updates&#10;Custom branding&#10;OBS Browser Source compatible" className="w-full input-field px-3 py-2 text-sm" data-testid="create-widget-features" />
          </div>
          {error && <p className="text-sm text-red-300">{error}</p>}
          <button type="submit" disabled={submitting} className="w-full btn-primary px-4 py-2.5 text-sm disabled:opacity-50 inline-flex items-center justify-center space-x-2" data-testid="create-widget-submit">
            <Plus className="w-4 h-4" />
            <span>{submitting ? 'Creating…' : 'Create as draft'}</span>
          </button>
        </form>
      </div>
    </div>
  );
};

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);

const WidgetFileManager = ({ widgetId, onFeedback, onConfigChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [overwriteConfirm, setOverwriteConfirm] = useState(null); // { filesToUpload, conflicts, dest }
  const [folderInput, setFolderInput] = useState('');
  const [entryInput, setEntryInput] = useState('');
  const [uploadPath, setUploadPath] = useState(''); // destination subfolder for new uploads
  const fileInputRef = React.useRef(null);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${ADMIN}/widgets/${widgetId}/files`, { withCredentials: true });
      setData(d);
      setFolderInput(d.folder || '');
      setEntryInput(d.html_file || '');
    } catch (e) {
      onFeedback('Failed to load files.', 'err');
    } finally { setLoading(false); }
  }, [widgetId, onFeedback]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const doUpload = async (files, overwrite = false, dest = uploadPath) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append('files', f));
    const params = new URLSearchParams({ overwrite: String(overwrite) });
    if (dest) params.set('path', dest);
    try {
      const { data: result } = await axios.post(
        `${ADMIN}/widgets/${widgetId}/files?${params.toString()}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const savedNames = result.saved.map((s) => s.name);
      const conflicts = result.skipped.filter((s) => s.reason === 'exists');
      if (conflicts.length && !overwrite) {
        // For overwrite re-upload, match by basename since backend echoes the destination path.
        const conflictBases = new Set(conflicts.map((c) => c.name.split('/').pop()));
        setOverwriteConfirm({
          filesToUpload: Array.from(files).filter((f) => conflictBases.has(f.name)),
          conflicts,
          dest,
        });
      } else if (savedNames.length) {
        const where = dest ? ` into ${dest}/` : '';
        onFeedback(`Uploaded ${savedNames.length} file${savedNames.length === 1 ? '' : 's'}${where}.`);
      }
      fetchFiles();
    } catch (e) {
      onFeedback(e.response?.data?.detail || 'Upload failed.', 'err');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSelectFiles = (e) => doUpload(e.target.files);
  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    doUpload(e.dataTransfer.files);
  };

  const deleteFile = async (name) => {
    if (!window.confirm(`Delete "${name}"? This can't be undone.`)) return;
    try {
      // Preserve slashes so backend matches the nested path; only encode each segment.
      const encoded = name.split('/').map(encodeURIComponent).join('/');
      await axios.delete(`${ADMIN}/widgets/${widgetId}/files/${encoded}`, { withCredentials: true });
      onFeedback(`Deleted ${name}.`);
      fetchFiles();
      onConfigChange();
    } catch (e) {
      onFeedback(e.response?.data?.detail || 'Delete failed.', 'err');
    }
  };

  const saveConfig = async () => {
    const body = {};
    if (folderInput && folderInput !== (data?.folder || '')) body.folder = folderInput;
    if (entryInput !== (data?.html_file || '')) body.html_file = entryInput;
    if (Object.keys(body).length === 0) {
      onFeedback('Nothing to save.', 'err');
      return;
    }
    try {
      await axios.patch(`${ADMIN}/widgets/${widgetId}/config`, body, { withCredentials: true });
      onFeedback('Widget config saved.');
      fetchFiles();
      onConfigChange();
    } catch (e) {
      onFeedback(e.response?.data?.detail || 'Save failed.', 'err');
    }
  };

  if (loading || !data) return <div className="text-sm text-muted py-3">Loading files…</div>;

  const htmlFiles = data.items.filter((f) => f.is_html);

  return (
    <div data-testid={`widget-files-${widgetId}`}>
      {/* Folder + Entry config row */}
      <div className="grid sm:grid-cols-2 gap-3 mb-5">
        <div>
          <label className="block text-xs uppercase tracking-wider text-faded mb-1">Folder slug</label>
          <input
            value={folderInput}
            onChange={(e) => setFolderInput(e.target.value)}
            placeholder="e.g. team-owner-scroll"
            className="w-full input-field px-3 py-2 text-sm font-mono"
            data-testid={`widget-folder-input-${widgetId}`}
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-faded mb-1">Entry HTML (served to OBS)</label>
          <select
            value={entryInput}
            onChange={(e) => setEntryInput(e.target.value)}
            className="w-full input-field px-3 py-2 text-sm"
            data-testid={`widget-entry-select-${widgetId}`}
          >
            <option value="">— pick an HTML file —</option>
            {htmlFiles.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
          </select>
        </div>
      </div>
      <div className="mb-5">
        <button
          onClick={saveConfig}
          className="btn-primary px-4 py-2 text-sm inline-flex items-center space-x-2"
          data-testid={`widget-save-config-${widgetId}`}
        >
          <Save className="w-4 h-4" /><span>Save folder & entry</span>
        </button>
      </div>

      {/* Upload destination + zone */}
      <div className="mb-3">
        <label className="block text-xs uppercase tracking-wider text-faded mb-1">
          Upload into
        </label>
        <div className="flex gap-2 items-center">
          <span className="text-sm text-faded font-mono shrink-0">{data.folder}/</span>
          <input
            value={uploadPath}
            onChange={(e) => setUploadPath(e.target.value.replace(/^\/+/, ''))}
            placeholder="(root) — or e.g. Team Icons/Football"
            className="flex-1 input-field px-3 py-2 text-sm font-mono"
            data-testid={`widget-upload-path-${widgetId}`}
          />
          {uploadPath && (
            <button
              onClick={() => setUploadPath('')}
              className="text-xs text-faded hover:text-strong px-2"
              data-testid={`widget-upload-path-clear-${widgetId}`}
            >
              clear
            </button>
          )}
        </div>
        <p className="text-xs text-faded mt-1">
          Subfolders are created automatically. Tip: click any folder below to set this.
        </p>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors mb-5 ${dragOver ? 'border-[#39FF14] bg-[#39FF14]/5' : 'border-default'}`}
        data-testid={`widget-drop-zone-${widgetId}`}
      >
        <UploadCloud className="w-8 h-8 mx-auto text-faded mb-2" />
        <p className="text-sm text-default mb-1">Drag & drop HTML, images, CSS, JS, fonts…</p>
        <p className="text-xs text-faded mb-3">
          Up to 100 MB per file. {uploadPath ? <>Destination: <span className="accent-green font-mono">{uploadPath}/</span></> : 'Destination: root.'}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleSelectFiles}
          className="hidden"
          data-testid={`widget-file-input-${widgetId}`}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="btn-secondary px-4 py-2 text-sm inline-flex items-center space-x-2 disabled:opacity-50"
          data-testid={`widget-pick-files-${widgetId}`}
        >
          {uploading ? <><Loader className="w-4 h-4 animate-spin" /><span>Uploading…</span></> : <><UploadCloud className="w-4 h-4" /><span>Choose files</span></>}
        </button>
      </div>

      {/* Overwrite confirmation */}
      {overwriteConfirm && (
        <div className="mb-5 p-4 rounded border border-yellow-500/40 bg-yellow-500/10">
          <p className="text-sm text-yellow-200 mb-3">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            These files already exist: <span className="font-mono">{overwriteConfirm.conflicts.map((c) => c.name).join(', ')}</span>
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => { doUpload(overwriteConfirm.filesToUpload, true, overwriteConfirm.dest); setOverwriteConfirm(null); }}
              className="px-3 py-1.5 text-xs bg-yellow-500/20 border border-yellow-500/40 text-yellow-100 rounded"
              data-testid={`widget-overwrite-confirm-${widgetId}`}
            >Overwrite</button>
            <button
              onClick={() => setOverwriteConfirm(null)}
              className="px-3 py-1.5 text-xs text-muted"
              data-testid={`widget-overwrite-cancel-${widgetId}`}
            >Cancel</button>
          </div>
        </div>
      )}

      {/* File list (grouped by folder) */}
      <h4 className="text-xs uppercase tracking-wider text-faded mb-2">Files ({data.items.length})</h4>
      {data.items.length === 0 ? (
        <p className="text-sm text-muted py-3">No files uploaded yet. Drop in your widget's HTML and any images/CSS/fonts it references.</p>
      ) : (
        <FileTree
          items={data.items}
          entryName={data.html_file}
          widgetId={widgetId}
          onDelete={deleteFile}
          activePath={uploadPath}
          onSetUploadPath={setUploadPath}
        />
      )}

      {/* Live preview iframe */}
      <WidgetPreview widgetId={widgetId} entryFile={data.html_file} />
    </div>
  );
};

const FileTree = ({ items, entryName, widgetId, onDelete, activePath, onSetUploadPath }) => {
  // Group by parent folder (POSIX path). Empty parent = root.
  const groups = React.useMemo(() => {
    const map = new Map();
    items.forEach((f) => {
      const key = f.parent || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    });
    // Sort keys: root first, then alphabetical
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === '') return -1;
      if (b === '') return 1;
      return a.localeCompare(b);
    });
  }, [items]);

  return (
    <div className="space-y-2.5" data-testid={`widget-file-list-${widgetId}`}>
      {groups.map(([parent, files]) => (
        <FolderGroup
          key={parent || '__root__'}
          parent={parent}
          files={files}
          entryName={entryName}
          widgetId={widgetId}
          onDelete={onDelete}
          isActiveUploadTarget={parent === activePath}
          onSetUploadPath={onSetUploadPath}
        />
      ))}
    </div>
  );
};

const FolderGroup = ({ parent, files, entryName, widgetId, onDelete, isActiveUploadTarget, onSetUploadPath }) => {
  // Subfolders collapsed by default to keep the list scannable.
  const [open, setOpen] = useState(parent === '');
  const isRoot = parent === '';

  return (
    <div>
      {!isRoot && (
        <div
          className={`w-full flex items-center justify-between gap-2 p-2 rounded border text-left transition-colors ${
            isActiveUploadTarget
              ? 'border-[#39FF14] bg-[#39FF14]/5'
              : 'border-subtle hover:border-default'
          }`}
          style={{ background: isActiveUploadTarget ? undefined : 'var(--bg-base)' }}
          data-testid={`folder-row-${widgetId}-${parent}`}
        >
          <button
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-2 min-w-0 flex-1 text-left"
            data-testid={`folder-toggle-${widgetId}-${parent}`}
          >
            <Folder className={`w-4 h-4 shrink-0 ${open ? 'accent-green' : 'text-faded'}`} />
            <span className="text-sm text-strong font-mono truncate">{parent}/</span>
          </button>
          <span className="text-xs text-faded shrink-0">{files.length} file{files.length === 1 ? '' : 's'}</span>
          <button
            onClick={() => onSetUploadPath(isActiveUploadTarget ? '' : parent)}
            className={`text-xs px-2 py-1 rounded border shrink-0 ${
              isActiveUploadTarget
                ? 'border-[#39FF14] text-[#39FF14]'
                : 'border-subtle text-faded hover:text-strong hover:border-default'
            }`}
            title={isActiveUploadTarget ? 'Clear upload target (back to root)' : 'Set as upload destination'}
            data-testid={`folder-set-target-${widgetId}-${parent}`}
          >
            {isActiveUploadTarget ? 'uploading here' : 'upload here'}
          </button>
        </div>
      )}
      {open && (
        <div className={`space-y-1.5 ${!isRoot ? 'mt-1.5 pl-3 border-l border-subtle ml-2' : ''}`}>
          {files.map((f) => {
            const Icon = fileIconFor(f.ext);
            const isEntry = f.name === entryName;
            return (
              <div key={f.name} className="flex items-center justify-between gap-3 p-2.5 rounded border border-subtle" style={{ background: 'var(--bg-base)' }}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <Icon className={`w-4 h-4 shrink-0 ${isEntry ? 'accent-green' : 'text-faded'}`} />
                  <span className="text-sm text-strong font-mono truncate">{f.basename || f.name}</span>
                  {isEntry && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-green-500/15 text-green-300 border-green-500/30 shrink-0">
                      entry
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-faded">{fmtBytes(f.size)}</span>
                  <button
                    onClick={() => onDelete(f.name)}
                    className="text-faded hover:text-red-300 p-1"
                    title="Delete"
                    data-testid={`widget-file-delete-${widgetId}-${f.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const WidgetPreview = ({ widgetId, entryFile }) => {
  const [show, setShow] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const previewSrc = `${ADMIN}/widgets/${widgetId}/preview?t=${bust}`;

  return (
    <div className="mt-6 pt-5 border-t border-subtle" data-testid={`widget-preview-section-${widgetId}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h4 className="text-xs uppercase tracking-wider text-faded inline-flex items-center gap-1.5">
          <FileCode className="w-3.5 h-3.5" /> Live preview
          {entryFile && <span className="text-faded normal-case tracking-normal font-mono">· {entryFile}</span>}
        </h4>
        <div className="flex gap-2">
          {show && (
            <button
              onClick={() => setBust(Date.now())}
              className="btn-secondary px-3 py-1.5 text-xs inline-flex items-center space-x-1.5"
              data-testid={`widget-preview-refresh-${widgetId}`}
            >
              <RefreshCw className="w-3.5 h-3.5" /><span>Refresh</span>
            </button>
          )}
          <button
            onClick={() => setShow((s) => !s)}
            className="btn-secondary px-3 py-1.5 text-xs"
            data-testid={`widget-preview-toggle-${widgetId}`}
          >
            {show ? 'Hide preview' : 'Show preview'}
          </button>
        </div>
      </div>
      {show && (
        <div className="rounded-lg overflow-hidden border border-default" style={{ background: '#000' }}>
          <iframe
            key={bust}
            src={previewSrc}
            title="Widget preview"
            className="w-full"
            style={{ height: '500px', border: 0 }}
            sandbox="allow-scripts allow-same-origin allow-forms"
            data-testid={`widget-preview-iframe-${widgetId}`}
          />
        </div>
      )}
    </div>
  );
};

const WidgetSubscribersPanel = ({ widgetId }) => {
  const [items, setItems] = useState(null);
  const [show, setShow] = useState(false);

  const load = async () => {
    if (items) { setShow(!show); return; }
    setShow(true);
    const { data } = await axios.get(`${ADMIN}/widgets/${widgetId}/subscribers`, { withCredentials: true });
    setItems(data.items || []);
  };

  return (
    <div className="mt-6 pt-5 border-t border-subtle">
      <button onClick={load} className="text-xs text-muted hover:text-strong inline-flex items-center space-x-1.5">
        <Users className="w-3.5 h-3.5" />
        <span>{show ? 'Hide subscribers' : 'Show subscribers'}</span>
      </button>
      {show && items !== null && (
        <div className="mt-3">
          {items.length === 0 ? (
            <p className="text-sm text-muted">No active subscribers yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {items.map((s) => (
                <li key={s.purchase_id} className="text-sm flex justify-between">
                  <span className="text-default">{s.user_name || '—'} · {s.user_email}</span>
                  <span className="text-faded text-xs">{fmtDate(s.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Audit log
// ─────────────────────────────────────────────────────────────────────
const AuditTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${ADMIN}/audit-log?limit=200`, { withCredentials: true });
        setItems(data.items || []);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-center py-10"><Loader className="w-6 h-6 accent-green animate-spin mx-auto" /></div>;

  return (
    <div data-testid="admin-audit">
      {items.length === 0 ? (
        <p className="text-sm text-muted text-center py-10">No admin actions logged yet.</p>
      ) : (
        <div className="card-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-faded border-b border-subtle">
              <tr>
                <th className="px-5 py-3">When</th>
                <th className="px-5 py-3">Actor</th>
                <th className="px-5 py-3">Action</th>
                <th className="px-5 py-3">Target</th>
                <th className="px-5 py-3">Details</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-subtle">
                  <td className="px-5 py-3 text-faded text-xs">{fmtDate(it.created_at)}</td>
                  <td className="px-5 py-3 text-default">{it.actor_email}</td>
                  <td className="px-5 py-3 accent-green text-xs">{it.action}</td>
                  <td className="px-5 py-3 text-muted text-xs">{it.target_type}/{it.target_id}</td>
                  <td className="px-5 py-3 text-muted text-xs font-mono">{JSON.stringify(it.details).slice(0, 80)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Email blast
// ─────────────────────────────────────────────────────────────────────
const BlastTab = () => {
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [audience, setAudience] = useState('all');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${ADMIN}/email-blast`, { subject, html, audience }, { withCredentials: true });
      setResult(data);
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Failed to send.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-blast">
      <div className="card-surface p-6">
        <h3 className="text-base font-semibold text-strong mb-2">Send a broadcast</h3>
        <p className="text-xs text-muted mb-5">Emails everyone in the selected audience via Resend. Use sparingly — they hear from you.</p>

        <form onSubmit={submit} className="space-y-4" data-testid="blast-form">
          <div>
            <label className="block text-xs text-faded mb-1">Audience</label>
            <select value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full input-field px-3 py-2 text-sm">
              <option value="all">All users</option>
              <option value="users">Customers only (role=user)</option>
              <option value="admins">Admins only</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-faded mb-1">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} required className="w-full input-field px-3 py-2 text-sm" data-testid="blast-subject" />
          </div>
          <div>
            <label className="block text-xs text-faded mb-1">HTML body</label>
            <textarea value={html} onChange={(e) => setHtml(e.target.value)} rows={10} required className="w-full input-field px-3 py-2 text-sm font-mono" placeholder="<h2>New widget available!</h2><p>Hi there...</p>" data-testid="blast-html" />
          </div>
          {error && <p className="text-sm text-red-300">{error}</p>}
          {result && (
            <div className="text-sm bg-green-500/10 border border-green-500/30 text-green-300 p-3 rounded">
              Sent to {result.sent} recipient{result.sent === 1 ? '' : 's'}. {result.failed > 0 && `${result.failed} failed.`}
            </div>
          )}
          <button type="submit" disabled={submitting} className="btn-primary px-4 py-2.5 text-sm disabled:opacity-50 inline-flex items-center space-x-2" data-testid="blast-submit">
            <Send className="w-4 h-4" />
            <span>{submitting ? 'Sending…' : 'Send blast'}</span>
          </button>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Careers — manage the single active job posting + view applications
// ─────────────────────────────────────────────────────────────────────
const CAREERS = `${API}/careers`;
const ADMIN_CAREERS = `${ADMIN}/careers`;

const CareersTab = () => {
  const [job, setJob] = useState(null);
  const [jobLoaded, setJobLoaded] = useState(false);
  const [intro, setIntro] = useState('');
  const [noticeText, setNoticeText] = useState('');
  const [noticeVisible, setNoticeVisible] = useState(false);
  const [introLoaded, setIntroLoaded] = useState(false);
  const [apps, setApps] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [openAppId, setOpenAppId] = useState(null);

  const showFeedback = (msg, kind = 'ok') => {
    setFeedback({ msg, kind });
    setTimeout(() => setFeedback(null), 5000);
  };

  const loadJob = useCallback(async () => {
    try {
      const { data } = await axios.get(`${ADMIN_CAREERS}/job`, { withCredentials: true });
      setJob(data.job || { title: '', description: '', active: false });
    } finally {
      setJobLoaded(true);
    }
  }, []);

  const loadIntro = useCallback(async () => {
    try {
      const { data } = await axios.get(`${ADMIN_CAREERS}/page-settings`, { withCredentials: true });
      setIntro(data.intro || '');
      setNoticeText(data.notice_text || '');
      setNoticeVisible(!!data.notice_visible);
    } finally {
      setIntroLoaded(true);
    }
  }, []);

  const loadApps = useCallback(async () => {
    const { data } = await axios.get(`${ADMIN_CAREERS}/applications`, { withCredentials: true });
    setApps(data.items || []);
  }, []);

  useEffect(() => { loadJob(); loadIntro(); loadApps(); }, [loadJob, loadIntro, loadApps]);

  const saveIntro = async () => {
    try {
      await axios.put(`${ADMIN_CAREERS}/page-settings`, {
        intro,
        notice_text: noticeText,
        notice_visible: noticeVisible,
      }, { withCredentials: true });
      showFeedback('Page settings saved.');
    } catch (e) {
      showFeedback(e.response?.data?.detail || 'Save failed.', 'err');
    }
  };

  const saveJob = async () => {
    if (!job.title.trim()) { showFeedback('Title is required.', 'err'); return; }
    try {
      await axios.put(`${ADMIN_CAREERS}/job`, {
        title: job.title.trim(),
        description: job.description || '',
        active: !!job.active,
      }, { withCredentials: true });
      showFeedback(`Saved. Job is ${job.active ? 'live' : 'hidden'} at /careers.`);
      loadJob();
    } catch (e) {
      showFeedback(e.response?.data?.detail || 'Save failed.', 'err');
    }
  };

  const clearJob = async () => {
    if (!window.confirm('Remove the job posting completely?')) return;
    await axios.delete(`${ADMIN_CAREERS}/job`, { withCredentials: true });
    setJob({ title: '', description: '', active: false });
    showFeedback('Posting cleared.');
  };

  const deleteApp = async (id) => {
    if (!window.confirm("Delete this application and all its uploaded files? Can't be undone.")) return;
    await axios.delete(`${ADMIN_CAREERS}/applications/${id}`, { withCredentials: true });
    showFeedback('Application deleted.');
    setOpenAppId(null);
    loadApps();
  };

  if (!jobLoaded || !introLoaded) {
    return <div className="text-center py-10"><Loader className="w-6 h-6 accent-green animate-spin mx-auto" /></div>;
  }

  const careersLink = `${BACKEND_URL.replace(/\/$/, '')}/careers`;

  return (
    <div className="space-y-6" data-testid="admin-careers">
      {feedback && (
        <div className={`p-3 rounded text-sm border ${feedback.kind === 'ok' ? 'bg-green-500/10 border-green-500/30 text-green-300' : 'bg-red-500/10 border-red-500/30 text-red-300'}`}>
          {feedback.msg}
        </div>
      )}

      {/* Page intro / subtitle + Notice banner */}
      <div className="card-surface p-5" data-testid="careers-intro-editor">
        <div className="mb-3">
          <h3 className="text-base font-semibold text-strong">Page intro</h3>
          <p className="text-xs text-muted mt-1">
            Short subtitle shown under the &quot;Available Positions&quot; heading on the public /careers page.
          </p>
        </div>
        <textarea
          value={intro}
          onChange={(e) => setIntro(e.target.value)}
          rows={3}
          maxLength={600}
          placeholder="LAB21G is small, focused, and growing. If something below fits — apply."
          className="input-field w-full px-3 py-2 text-sm leading-relaxed"
          data-testid="careers-intro-input"
        />
        <div className="text-xs text-faded mt-1">{intro.length}/600</div>

        <div className="mt-6 pt-5 border-t border-subtle">
          <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
            <div>
              <h3 className="text-base font-semibold text-strong inline-flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" style={{ color: '#FACC15' }} />
                Important notice
              </h3>
              <p className="text-xs text-muted mt-1">
                Optional attention-grabbing banner shown above the job posting. Supports markdown.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="careers-notice-toggle">
              <input
                type="checkbox"
                checked={noticeVisible}
                onChange={(e) => setNoticeVisible(e.target.checked)}
              />
              <span style={{ color: noticeVisible ? '#FACC15' : 'var(--text-muted)' }}>
                {noticeVisible ? 'Visible' : 'Hidden'}
              </span>
            </label>
          </div>
          <MarkdownEditor
            value={noticeText}
            onChange={setNoticeText}
            rows={4}
            placeholder="e.g. **Heads up:** We're hiring fast — applications submitted by Friday get first review."
            testid="careers-notice"
          />
          <div className="text-xs text-faded mt-1">{noticeText.length}/2000</div>
        </div>

        <div className="flex justify-end mt-4">
          <button
            onClick={saveIntro}
            className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2"
            data-testid="careers-intro-save"
          >
            <Save className="w-4 h-4" /><span>Save page settings</span>
          </button>
        </div>
      </div>

      {/* Job editor */}
      <div className="card-surface p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="text-base font-semibold text-strong">Job posting</h3>
            <p className="text-xs text-muted mt-1">
              The public page lives at <a href={careersLink} target="_blank" rel="noreferrer" className="accent-green hover:underline">{careersLink}</a> — share that link with applicants.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="job-active-toggle">
            <input
              type="checkbox"
              checked={!!job?.active}
              onChange={(e) => setJob({ ...job, active: e.target.checked })}
            />
            <span className={job?.active ? 'accent-green' : 'text-muted'}>{job?.active ? 'Live' : 'Hidden'}</span>
          </label>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-faded mb-1">Title</label>
            <input
              value={job?.title || ''}
              onChange={(e) => setJob({ ...job, title: e.target.value })}
              placeholder="e.g. Live Stream Producer"
              className="input-field w-full px-3 py-2 text-sm"
              data-testid="job-title-input"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-faded mb-1">Description</label>
            <MarkdownEditor
              value={job?.description || ''}
              onChange={(v) => setJob({ ...job, description: v })}
              rows={12}
              placeholder="What the role is, what you're looking for, schedule, pay range, perks, etc. Use the toolbar for bold, italics, lists, etc."
              testid="job-description"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={saveJob} className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="job-save">
              <Save className="w-4 h-4" /><span>Save posting</span>
            </button>
            {job?.title && (
              <button onClick={clearJob} className="px-4 py-2 text-sm inline-flex items-center gap-2 border border-red-500/30 text-red-300 rounded hover:bg-red-500/10" data-testid="job-clear">
                <Trash2 className="w-4 h-4" /><span>Clear posting</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Applications */}
      <div className="card-surface p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="text-base font-semibold text-strong">Applications <span className="text-faded font-normal">({apps.length})</span></h3>
          <button onClick={loadApps} className="btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1.5" data-testid="apps-refresh">
            <RefreshCw className="w-3.5 h-3.5" /><span>Refresh</span>
          </button>
        </div>

        {apps.length === 0 ? (
          <p className="text-sm text-muted py-3">No applications yet.</p>
        ) : (
          <div className="space-y-2" data-testid="apps-list">
            {apps.map((a) => (
              <ApplicationRow
                key={a.id}
                app={a}
                isOpen={openAppId === a.id}
                onToggle={() => setOpenAppId(openAppId === a.id ? null : a.id)}
                onDelete={() => deleteApp(a.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ApplicationRow = ({ app, isOpen, onToggle, onDelete }) => {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (isOpen && !detail) {
      axios.get(`${ADMIN_CAREERS}/applications/${app.id}`, { withCredentials: true })
        .then((r) => setDetail(r.data.application))
        .catch(() => setDetail({ error: true }));
    }
  }, [isOpen, detail, app.id]);

  return (
    <div className="card-surface" style={{ padding: 0 }}>
      <button
        onClick={onToggle}
        className="w-full text-left p-4 flex items-center justify-between gap-3 hover:bg-white/[0.02]"
        data-testid={`app-row-${app.id}`}
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold text-strong">{app.first_name} {app.last_name}</p>
          <p className="text-xs text-muted mt-0.5">
            {app.email} {app.phone && <>· {app.phone}</>} · {fmtDate(app.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {app.documents > 0 && (
            <span className="text-xs text-faded inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" />{app.documents}</span>
          )}
          {app.has_video && (
            <span className="text-xs accent-green inline-flex items-center gap-1"><FileIcon className="w-3.5 h-3.5" />video</span>
          )}
          {app.youtube_url && (
            <span className="text-xs accent-green inline-flex items-center gap-1"><FileIcon className="w-3.5 h-3.5" />YT</span>
          )}
          <Eye className={`w-4 h-4 ${isOpen ? 'accent-green' : 'text-faded'}`} />
        </div>
      </button>
      {isOpen && (
        <div className="p-4 border-t border-subtle">
          {!detail ? (
            <Loader className="w-5 h-5 accent-green animate-spin" />
          ) : detail.error ? (
            <p className="text-sm text-red-300">Failed to load.</p>
          ) : (
            <div className="space-y-3 text-sm">
              {detail.location && (
                <p className="text-muted"><span className="text-faded">Location:</span> {detail.location}</p>
              )}
              {detail.message && (
                <div className="p-3 rounded border border-subtle whitespace-pre-wrap text-default" style={{ background: 'var(--bg-base)' }}>
                  {detail.message}
                </div>
              )}

              {detail.documents.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-faded mb-1.5">Documents</p>
                  {detail.documents.map((d) => (
                    <a key={d.name} href={d.download_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2 rounded border border-subtle hover:border-default mb-1" style={{ background: 'var(--bg-base)' }}>
                      <FileText className="w-4 h-4 text-faded" />
                      <span className="text-sm text-strong font-mono truncate flex-1">{d.name}</span>
                      <span className="text-xs text-faded">{fmtBytes(d.size)}</span>
                      <Download className="w-4 h-4 accent-green" />
                    </a>
                  ))}
                </div>
              )}

              {detail.video && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-faded mb-1.5">Video upload</p>
                  <a href={detail.video.download_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2 rounded border border-subtle hover:border-default" style={{ background: 'var(--bg-base)' }}>
                    <FileIcon className="w-4 h-4 text-faded" />
                    <span className="text-sm text-strong font-mono truncate flex-1">{detail.video.name}</span>
                    <span className="text-xs text-faded">{fmtBytes(detail.video.size)}</span>
                    <Download className="w-4 h-4 accent-green" />
                  </a>
                </div>
              )}

              {detail.youtube_url && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-faded mb-1.5">YouTube link</p>
                  <a href={detail.youtube_url} target="_blank" rel="noreferrer" className="text-sm accent-green hover:underline break-all inline-flex items-center gap-1.5">
                    <FileIcon className="w-4 h-4" />{detail.youtube_url}
                  </a>
                </div>
              )}

              <div className="flex gap-2 pt-3 border-t border-subtle">
                <a href={`mailto:${detail.email}`} className="btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />Email
                </a>
                <button onClick={onDelete} className="px-3 py-1.5 text-xs inline-flex items-center gap-1.5 border border-red-500/30 text-red-300 rounded hover:bg-red-500/10" data-testid={`app-delete-${app.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />Delete
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminConsole;
